import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ExternalLink, Loader2, CheckSquare, Truck, Calendar } from 'lucide-react';

/**
 * Shared form component for editing gift suggestions.
 * Used by both AIGiftSuggestions (AI edit mode) and GiftSuggestions (manual entry).
 */

export interface GiftFormData {
  name: string;
  description: string;
  price: string;
  url: string;
  imageUrl: string;
  shippingCost: string;
  estimatedDeliveryDays: string;
  suggestedOrderDate: string;
}

interface GiftSuggestionFormProps {
  /** Form data */
  data: GiftFormData;
  /** Callback when any field changes */
  onChange: (data: GiftFormData) => void;
  /** Callback when the form is submitted */
  onSubmit: () => void;
  /** Callback when cancel is clicked */
  onCancel: () => void;
  /** Whether the form is currently submitting */
  isSubmitting?: boolean;
  /** Optional label for the submit button */
  submitLabel?: string;
  /** Optional label for the cancel button */
  cancelLabel?: string;
  /** Whether this is an AI-generated suggestion */
  isAISuggestion?: boolean;
  /** Optional header to show */
  header?: {
    title: string;
    subtitle?: string;
  };
  /** Optional additional content (e.g., giftee selector) to render after the main fields */
  additionalContent?: React.ReactNode;
}

export function GiftSuggestionForm({
  data,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = 'Add Suggestion',
  cancelLabel = 'Cancel',
  isAISuggestion = false,
  header,
  additionalContent,
}: GiftSuggestionFormProps) {
  const handleChange = (field: keyof GiftFormData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <Card className="p-6 border-2 border-primary/20 shadow-lg">
      {/* Header */}
      {header && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl text-primary font-semibold">{header.title}</h2>
            {header.subtitle && (
              <p className="text-sm text-muted-foreground">{header.subtitle}</p>
            )}
          </div>
          {isAISuggestion && (
            <Badge variant="outline" className="bg-purple-50">
              AI Suggested
            </Badge>
          )}
        </div>
      )}

      <div className="space-y-4">
        {/* Additional content (e.g., giftee selector) - placed at top per user request */}
        {additionalContent}

        {/* Product Name */}
        <div>
          <Label htmlFor="form-name" className="text-sm font-medium">
            Product Name *
          </Label>
          <Input
            id="form-name"
            value={data.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('name', e.target.value)
            }
            placeholder="Enter product name"
            className="mt-1"
          />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="form-description" className="text-sm font-medium">
            Description
          </Label>
          <Textarea
            id="form-description"
            value={data.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleChange('description', e.target.value)
            }
            placeholder="Add a description"
            className="mt-1 min-h-[80px]"
          />
        </div>

        {/* Price and Shipping Cost - side by side on larger screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="form-price" className="text-sm font-medium">
              Price (USD) *
            </Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                id="form-price"
                type="number"
                value={data.price}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('price', e.target.value)
                }
                placeholder="0.00"
                className="pl-7"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="form-shipping-cost" className="text-sm font-medium">
              Shipping Cost (USD)
            </Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                id="form-shipping-cost"
                type="number"
                value={data.shippingCost}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('shippingCost', e.target.value)
                }
                placeholder="0.00"
                className="pl-7"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Shipping Days and Order By Date - side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="form-shipping-days" className="text-sm font-medium">
              <div className="flex items-center gap-1">
                <Truck className="w-4 h-4" />
                Estimated Shipping Days
              </div>
            </Label>
            <Input
              id="form-shipping-days"
              type="number"
              value={data.estimatedDeliveryDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('estimatedDeliveryDays', e.target.value)
              }
              placeholder="e.g., 5"
              className="mt-1"
              min="0"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Number of days for shipping after order is placed
            </p>
          </div>

          <div>
            <Label htmlFor="form-order-by-date" className="text-sm font-medium">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Order By Date
              </div>
            </Label>
            <Input
              id="form-order-by-date"
              type="date"
              value={data.suggestedOrderDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('suggestedOrderDate', e.target.value)
              }
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Last date to order for on-time delivery (overrides Estimated Shipping Days if set)
            </p>
          </div>
        </div>

        {/* Product URL */}
        <div>
          <Label htmlFor="form-url" className="text-sm font-medium">
            Product URL
          </Label>
          <Input
            id="form-url"
            value={data.url}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('url', e.target.value)
            }
            placeholder="https://..."
            className="mt-1"
          />
          {data.url && (
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Open link to verify
            </a>
          )}
        </div>

        {/* Image URL */}
        <div>
          <Label htmlFor="form-image" className="text-sm font-medium">
            Image URL (optional)
          </Label>
          <Input
            id="form-image"
            value={data.imageUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('imageUrl', e.target.value)
            }
            placeholder="https://..."
            className="mt-1"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6 pt-4 border-t">
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || !data.name}
          className="flex-1 bg-primary"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckSquare className="w-4 h-4 mr-2" />
              {submitLabel}
            </>
          )}
        </Button>

        <Button onClick={onCancel} variant="outline" disabled={isSubmitting}>
          {cancelLabel}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Helper function to create empty form data
 */
export function createEmptyGiftFormData(): GiftFormData {
  return {
    name: '',
    description: '',
    price: '',
    url: '',
    imageUrl: '',
    shippingCost: '',
    estimatedDeliveryDays: '',
    suggestedOrderDate: '',
  };
}

/**
 * Helper function to convert form data to API format
 */
export function formDataToApiPayload(data: GiftFormData, source: 'ai' | 'manual' = 'manual') {
  // Parse price - return undefined if invalid
  const parsedPrice = data.price ? parseFloat(data.price) : undefined;
  const price = parsedPrice !== undefined && !isNaN(parsedPrice) ? parsedPrice : undefined;

  // Parse shipping cost - return undefined if invalid
  const parsedShippingCost = data.shippingCost ? parseFloat(data.shippingCost) : undefined;
  const shippingCost =
    parsedShippingCost !== undefined && !isNaN(parsedShippingCost)
      ? parsedShippingCost
      : undefined;

  // Parse estimated delivery days - return undefined if invalid
  const parsedDays = data.estimatedDeliveryDays
    ? parseInt(data.estimatedDeliveryDays, 10)
    : undefined;
  const estimatedDeliveryDays =
    parsedDays !== undefined && !isNaN(parsedDays) ? parsedDays : undefined;

  return {
    url: data.url,
    name: data.name,
    description: data.description,
    price,
    imageUrl: data.imageUrl || undefined,
    source,
    shippingCost,
    estimatedDeliveryDays,
    suggestedOrderDate: data.suggestedOrderDate || undefined,
  };
}
